import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "./api.ts";
import { stringify } from "./frontmatter.ts";
import type { IndexItem, FolderMeta } from "./types.ts";

const CAT_ORDER = ["Journey", "Homelab", "Selfhost", "Access"];

/** ASCII slug for a URL path segment. */
function slugify(s: string): string {
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
const parentOf = (dir: string) => dir.slice(0, dir.lastIndexOf("/"));
const segOf = (dir: string) => dir.slice(dir.lastIndexOf("/") + 1);
/** Docusaurus doc id: path under docs/ without extension (e.g. journey/security/intro). */
const docIdOf = (path: string) => path.replace(/^docs\//, "").replace(/\.mdx?$/, "");

/** Docusaurus link 的四態：無 link 鍵＝auto（慣例檔生效）、link:null＝明確關閉。 */
type LinkMode = "auto" | "none" | "index" | "doc";
type DocRef = { path: string; title: string; id: string; sha: string; status: string };

type WorkFolder = {
  dir: string;
  file: string;               // _category_.json path
  sha: string | null;         // null → file doesn't exist yet (create on save)
  raw: Record<string, any>;
  docs: DocRef[];
  label: string;
  position: number | null;
  collapsed: boolean | null;
  collapsible: boolean | null;
  linkMode: LinkMode;
  linkDocId: string | null;
  linkTitle: string | null;      // generated-index page title
  linkDesc: string | null;       // generated-index page description（＝路徑頁的內文）
  indexDoc: string | null;       // Docusaurus 慣例索引檔（index/README/與資料夾同名）的路徑
  // originals for dirty-check
  origLabel: string;
  origPosition: number | null;
  origCollapsed: boolean | null;
  origCollapsible: boolean | null;
  origLinkMode: LinkMode;
  origLinkDocId: string | null;
  origLinkTitle: string | null;
  origLinkDesc: string | null;
};

type DocLite = { path: string; title: string; eff: number };
type Node =
  | { kind: "doc"; pos: number; doc: DocLite }
  | { kind: "folder"; pos: number; f: WorkFolder };

function withEff(docs: IndexItem[]): DocLite[] {
  const sorted = docs
    .slice()
    .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999) || a.path.localeCompare(b.path, undefined, { numeric: true }));
  let prev = 0;
  return sorted.map((it) => {
    let eff = typeof it.order === "number" ? it.order : prev + 1;
    if (eff <= prev) eff = prev + 1;
    prev = eff;
    return { path: it.path, title: it.title, eff };
  });
}

function between(prev: number | null, next: number | null): number {
  if (prev == null && next == null) return 1;
  if (prev == null) return next! > 1 ? Number((next! - 1).toFixed(3)) : Number((next! / 2).toFixed(3));
  if (next == null) return Number((prev + 1).toFixed(3));
  return Number(((prev + next) / 2).toFixed(3));
}

function readLink(raw: Record<string, any>): { mode: LinkMode; id: string | null; title: string | null; desc: string | null } {
  if (!raw || !("link" in raw)) return { mode: "auto", id: null, title: null, desc: null };
  const link = raw.link;
  if (link === null) return { mode: "none", id: null, title: null, desc: null };
  if (link && typeof link === "object") {
    if (link.type === "generated-index")
      return {
        mode: "index",
        id: null,
        title: typeof link.title === "string" ? link.title : null,
        desc: typeof link.description === "string" ? link.description : null,
      };
    if (link.type === "doc")
      return { mode: "doc", id: typeof link.id === "string" ? link.id : null, title: null, desc: null };
  }
  return { mode: "auto", id: null, title: null, desc: null };
}
/** doc-mode with no doc chosen falls back to auto (never writes an invalid link). */
const effMode = (f: { linkMode: LinkMode; linkDocId: string | null }): LinkMode =>
  f.linkMode === "doc" && !f.linkDocId ? "auto" : f.linkMode;

/** 一致的標記：這顆按鈕會馬上 commit，不經過上面的「儲存／捨棄」暫存流程。 */
function InstantBadge() {
  return <span className="fm-instant" aria-hidden>⚡ 立即</span>;
}

function isDirty(f: WorkFolder): boolean {
  const m = effMode(f);
  return (
    f.label !== f.origLabel ||
    f.position !== f.origPosition ||
    (f.collapsed ?? null) !== (f.origCollapsed ?? null) ||
    (f.collapsible ?? null) !== (f.origCollapsible ?? null) ||
    m !== f.origLinkMode ||
    (m === "doc" && f.linkDocId !== f.origLinkDocId) ||
    (m === "index" &&
      ((f.linkTitle ?? "") !== (f.origLinkTitle ?? "") || (f.linkDesc ?? "") !== (f.origLinkDesc ?? "")))
  );
}

export function FolderManager(props: {
  categories: string[];
  items: IndexItem[];
  folders: FolderMeta[];
  treeSha: string;
  canEdit: boolean;
  onClose: () => void;
  onReload: () => void;
  onOpenDoc: (path: string) => void;
}) {
  const cats = useMemo(
    () =>
      props.categories
        .filter((c) => c !== "Blog")
        .sort((a, b) => {
          const ia = CAT_ORDER.indexOf(a), ib = CAT_ORDER.indexOf(b);
          return (ia < 0 ? 9 : ia) - (ib < 0 ? 9 : ib) || a.localeCompare(b);
        }),
    [props.categories]
  );

  const [cat, setCat] = useState<string>(cats[0] ?? "");
  const [workByCat, setWorkByCat] = useState<Record<string, WorkFolder[]>>({});
  const [panelOpen, setPanelOpen] = useState<Set<string>>(new Set());
  const [renaming, setRenaming] = useState<{ dir: string; text: string } | null>(null);
  const [slugDrafts, setSlugDrafts] = useState<Record<string, string>>({});
  const [creating, setCreating] = useState<string | null>(null); // new-path name draft
  const [confirmDel, setConfirmDel] = useState<WorkFolder | null>(null);
  const [confirmDelDoc, setConfirmDelDoc] = useState<WorkFolder | null>(null);
  const [confirmRename, setConfirmRename] = useState<{ f: WorkFolder; to: string } | null>(null);
  const [dangerOpen, setDangerOpen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const treeRef = useRef<HTMLDivElement>(null);
  const [dragDir, setDragDir] = useState<string | null>(null);
  const [ghost, setGhost] = useState<{ x: number; y: number } | null>(null);
  const [hoverGap, setHoverGap] = useState<number | null>(null);
  // 拖曳中的即時指標位置＋捲動迴圈——`.nf-tree` 沒有自己的捲軸，整頁靠瀏覽器捲動，
  // 手指/滑鼠按著不放時原生不會自動捲動，所以拖到螢幕邊緣時手動捲頁，range 才涵蓋完整清單。
  const pointerYRef = useRef(0);
  const autoScrollRAF = useRef<number | null>(null);

  const catInfo = useMemo(() => {
    let base = `docs/${cat.toLowerCase()}`;
    const byDir = new Map<string, IndexItem[]>();
    for (const it of props.items) {
      if (it.category !== cat || !it.path.startsWith("docs/")) continue;
      base = it.path.split("/").slice(0, 2).join("/");
      const dir = it.path.slice(0, it.path.lastIndexOf("/"));
      (byDir.get(dir) ?? byDir.set(dir, []).get(dir)!).push(it);
    }
    const rootDocs = withEff(byDir.get(base) ?? []);
    const metas = props.folders.filter((f) => f.category === cat);
    const subDirs = new Set<string>();
    for (const d of byDir.keys()) if (d !== base) subDirs.add(d);
    for (const f of metas) subDirs.add(f.dir);
    const initial: WorkFolder[] = [...subDirs].map((dir) => {
      const meta = metas.find((f) => f.dir === dir);
      const raw = meta?.raw ?? {};
      const label = meta?.label || segOf(dir);
      const position = meta?.position ?? null;
      const collapsed = meta?.collapsed ?? null;
      const collapsible = typeof raw.collapsible === "boolean" ? raw.collapsible : null;
      const { mode, id, title, desc } = readLink(raw);
      const items = byDir.get(dir) ?? [];
      const docs: DocRef[] = items.map((it) => ({ path: it.path, title: it.title, id: docIdOf(it.path), sha: it.sha, status: it.status }));
      // Docusaurus 慣例：資料夾裡叫 index/README 或與資料夾同名的 doc 會自動當索引頁。
      const conv = items.find((it) => {
        const fn = (it.path.split("/").pop() ?? "").replace(/\.mdx?$/, "").toLowerCase();
        return fn === "index" || fn === "readme" || fn === segOf(dir).toLowerCase();
      });
      return {
        dir,
        file: meta?.file ?? `${dir}/_category_.json`,
        sha: meta?.sha ?? null,
        raw,
        docs,
        label, position, collapsed, collapsible,
        linkMode: mode, linkDocId: id, linkTitle: title, linkDesc: desc,
        indexDoc: conv?.path ?? null,
        origLabel: label, origPosition: position, origCollapsed: collapsed, origCollapsible: collapsible,
        origLinkMode: mode, origLinkDocId: id, origLinkTitle: title, origLinkDesc: desc,
      };
    });
    return { base, rootDocs, initial };
  }, [props.items, props.folders, cat]);

  useEffect(() => {
    setWorkByCat((prev) => (prev[cat] ? prev : { ...prev, [cat]: catInfo.initial }));
  }, [cat, catInfo]);

  // After a write the parent reloads → treeSha changes → drop staged state so the
  // tree re-bases on fresh files/shas, but keep the selected category.
  useEffect(() => {
    setWorkByCat({});
    setPanelOpen(new Set());
    setDangerOpen(new Set());
    setSlugDrafts({});
    setCreating(null);
    setConfirmDel(null);
    setConfirmDelDoc(null);
    setConfirmRename(null);
  }, [props.treeSha]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  // Esc 關閉任何開著的確認框（改名輸入框、新增路徑輸入框自己已經處理 Esc 了）。
  useEffect(() => {
    if (!confirmRename && !confirmDel && !confirmDelDoc) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (confirmRename) setConfirmRename(null);
      else if (confirmDel) setConfirmDel(null);
      else if (confirmDelDoc) setConfirmDelDoc(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirmRename, confirmDel, confirmDelDoc]);

  const work = workByCat[cat] ?? catInfo.initial;

  const updateFolder = (dir: string, patch: Partial<WorkFolder>) =>
    setWorkByCat((prev) => ({
      ...prev,
      [cat]: (prev[cat] ?? catInfo.initial).map((f) => (f.dir === dir ? { ...f, ...patch } : f)),
    }));

  const rootSeq: Node[] = useMemo(() => {
    const nodes: Node[] = [
      ...catInfo.rootDocs.map((d) => ({ kind: "doc", pos: d.eff, doc: d } as Node)),
      ...work.map((f) => ({ kind: "folder", pos: f.position ?? 9999, f } as Node)),
    ];
    return nodes.sort(
      (a, b) =>
        a.pos - b.pos ||
        (a.kind === "folder" ? 1 : 0) - (b.kind === "folder" ? 1 : 0) ||
        keyOf(a).localeCompare(keyOf(b), undefined, { numeric: true })
    );
  }, [catInfo.rootDocs, work]);

  const changes = useMemo(
    () =>
      Object.values(workByCat)
        .flat()
        .filter((f) => isDirty(f)),
    [workByCat]
  );
  const dirty = changes.length > 0;

  // ---- drag to reorder ----
  // 重要：被拖的那一列「不能」從清單移除。曾經把它 filter 掉，結果拖曳一開始
  // 該列就 unmount、pointer capture 隨元素消失而失效，事件流全斷——拖曳完全動不了，
  // 而且 dragDir 卡死、畫面停在拖曳狀態沒有出口。現在該列原地保留（dragsrc 半透明），
  // 事件監聽掛在 window（見下方 useEffect），跟個別元素的生死脫鉤。
  function nearestGap(y: number): number {
    const gaps = treeRef.current?.querySelectorAll<HTMLElement>(".nf-gap") ?? [];
    let best = 0, bestDist = Infinity;
    gaps.forEach((g) => {
      const r = g.getBoundingClientRect();
      const d = Math.abs(r.top + r.height / 2 - y);
      if (d < bestDist) { bestDist = d; best = Number(g.dataset.pidx); }
    });
    return best;
  }

  // 靠近視窗上/下緣時捲頁；速度隨越靠近邊緣而加快。停手（onHandleUp）或元件卸載時停止。
  //
  // 手機上原本「拖到最上面沒反應」的根因：頂部的 sticky topbar（含返回/儲存鍵）浮在畫面最上方，
  // 觸發捲頁的門檻卻是抓固定 72px——比 topbar 實際高度（含安全區）還矮，等於門檻有一截
  // 被 topbar 蓋住，手指伸過去看不到目標、也永遠進不了「該加速捲頁」的區。開始拖曳時先量
  // topbar 實際佔用的高度，捲頁門檻永遠留在它下面，才能真正拖到清單最頂端。
  function stopAutoScroll() {
    if (autoScrollRAF.current != null) {
      cancelAnimationFrame(autoScrollRAF.current);
      autoScrollRAF.current = null;
    }
  }
  function startAutoScroll() {
    if (autoScrollRAF.current != null) return;
    const topBar = document.querySelector(".editor > .topbar");
    const topEdge = (topBar?.getBoundingClientRect().bottom ?? 0) + 32;
    const BOTTOM_EDGE = 72, MAX_SPEED = 18;
    const step = () => {
      const y = pointerYRef.current;
      let dy = 0;
      if (y < topEdge) dy = -MAX_SPEED * Math.min(1, (topEdge - y) / topEdge || 1);
      else if (y > window.innerHeight - BOTTOM_EDGE) dy = MAX_SPEED * (1 - (window.innerHeight - y) / BOTTOM_EDGE);
      if (dy !== 0) {
        window.scrollBy(0, dy);
        setHoverGap(nearestGap(Math.max(y, topEdge)));
      }
      autoScrollRAF.current = requestAnimationFrame(step);
    };
    autoScrollRAF.current = requestAnimationFrame(step);
  }
  useEffect(() => stopAutoScroll, []); // 保險：元件卸載時一定停掉 RAF

  function onHandleDown(e: React.PointerEvent, dir: string) {
    e.preventDefault();
    setDragDir(dir);
    pointerYRef.current = e.clientY;
    setGhost({ x: e.clientX, y: e.clientY });
    setHoverGap(null);
    startAutoScroll();
  }

  // 放開時的落點計算：鄰居一律跳過被拖的資料夾自己；落在自己旁邊的兩個縫＝沒有移動。
  function applyDrop(gapIdx: number) {
    if (!dragDir) return;
    const draggedIdx = rootSeq.findIndex((n) => n.kind === "folder" && n.f.dir === dragDir);
    if (gapIdx === draggedIdx || gapIdx === draggedIdx + 1) return; // 放回原位＝取消
    let prev: number | null = null, next: number | null = null;
    for (let i = gapIdx - 1; i >= 0; i--) {
      const n = rootSeq[i];
      if (!(n.kind === "folder" && n.f.dir === dragDir)) { prev = n.pos; break; }
    }
    for (let i = gapIdx; i < rootSeq.length; i++) {
      const n = rootSeq[i];
      if (!(n.kind === "folder" && n.f.dir === dragDir)) { next = n.pos; break; }
    }
    updateFolder(dragDir, { position: between(prev, next) });
  }

  // 拖曳中的事件一律掛 window：pointermove 更新 ghost/落點、pointerup 套用、
  // pointercancel（系統中斷手勢）與 Esc 中止不套用——PC 也因此有了明確的取消方式。
  useEffect(() => {
    if (!dragDir) return;
    const finish = (apply: boolean) => {
      stopAutoScroll();
      if (apply) applyDrop(nearestGap(pointerYRef.current));
      setDragDir(null);
      setGhost(null);
      setHoverGap(null);
    };
    const onMove = (e: PointerEvent) => {
      pointerYRef.current = e.clientY;
      setGhost({ x: e.clientX, y: e.clientY });
      setHoverGap(nearestGap(e.clientY));
    };
    const onUp = () => finish(true);
    const onCancel = () => finish(false);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") finish(false); };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragDir]);

  // 鍵盤排序：把 ⠿ 做成真正的 button，聚焦後按 ↑↓ 跟鄰居互換一格（跟放開拖曳時同一套
  // between() 中點邏輯）——不必另外放一組上下按鈕，一個控件兩種操作方式。
  function moveFolder(dir: string, delta: -1 | 1) {
    const i = rootSeq.findIndex((n) => n.kind === "folder" && n.f.dir === dir);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= rootSeq.length) return;
    const prevPos = delta < 0 ? (j > 0 ? rootSeq[j - 1].pos : null) : rootSeq[j].pos;
    const nextPos = delta < 0 ? rootSeq[j].pos : (j + 1 < rootSeq.length ? rootSeq[j + 1].pos : null);
    updateFolder(dir, { position: between(prevPos, nextPos) });
  }
  function onHandleKeyDown(e: React.KeyboardEvent, dir: string) {
    if (e.key === "ArrowUp") { e.preventDefault(); moveFolder(dir, -1); }
    else if (e.key === "ArrowDown") { e.preventDefault(); moveFolder(dir, 1); }
  }

  function togglePanel(dir: string) {
    setPanelOpen((prev) => {
      const next = new Set(prev);
      next.has(dir) ? next.delete(dir) : next.add(dir);
      return next;
    });
  }
  function toggleDanger(dir: string) {
    setDangerOpen((prev) => {
      const next = new Set(prev);
      next.has(dir) ? next.delete(dir) : next.add(dir);
      return next;
    });
  }
  function commitRename() {
    if (!renaming) return;
    const t = renaming.text.trim();
    if (t) updateFolder(renaming.dir, { label: t });
    setRenaming(null);
  }
  function discard() {
    setWorkByCat({});
    setNotice("已捨棄未儲存的變更。");
  }

  // ---- writes ----
  async function guardEdit(): Promise<boolean> {
    if (props.canEdit) return true;
    setNotice("訪客唯讀模式——這個動作不會送出，登入後即可編輯路徑。");
    return false;
  }

  async function save() {
    if (!(await guardEdit()) || !dirty || busy) return;
    setBusy(true); setError(null);
    try {
      for (const f of changes) {
        const obj: Record<string, any> = { ...f.raw };
        obj.label = f.label;
        if (f.position != null) obj.position = f.position;
        if (f.collapsed != null) obj.collapsed = f.collapsed; else delete obj.collapsed;
        if (f.collapsible != null) obj.collapsible = f.collapsible; else delete obj.collapsible;
        const mode = effMode(f);
        if (mode === "auto") delete obj.link;              // 無 link 鍵＝交給慣例檔
        else if (mode === "none") obj.link = null;         // link:null＝明確關閉
        else if (mode === "index") {
          const link: Record<string, any> = { type: "generated-index" };
          if (f.linkTitle && f.linkTitle.trim()) link.title = f.linkTitle.trim();
          if (f.linkDesc && f.linkDesc.trim()) link.description = f.linkDesc.trim();
          obj.link = link;
        } else obj.link = { type: "doc", id: f.linkDocId };
        await api.save({
          path: f.file,
          content: JSON.stringify(obj, null, 2) + "\n",
          sha: f.sha ?? undefined,
          message: `content: update category ${f.dir}`,
        });
      }
      setNotice("已儲存。");
      props.onReload();
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  async function createPath() {
    if (!(await guardEdit()) || busy) return;
    const name = (creating ?? "").trim();
    const slug = slugify(name);
    if (!slug) { setNotice("請先輸入路徑名稱（英文小寫）。"); return; }
    const dir = `${catInfo.base}/${slug}`;
    if (work.some((f) => f.dir === dir)) { setNotice("這個路徑已存在。"); return; }
    setBusy(true); setError(null);
    try {
      const endPos = Math.max(0, ...rootSeq.map((n) => n.pos).filter((p) => p < 9999)) + 1;
      const obj = { label: name, position: endPos };
      await api.save({
        path: `${dir}/_category_.json`,
        content: JSON.stringify(obj, null, 2) + "\n",
        message: `content: create category ${dir}`,
      });
      setCreating(null);
      setNotice("已建立路徑。加入文章後才會顯示在網站上。");
      props.onReload();
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  // 只開確認框——實際搬檔在 confirmRenamePath()，讓使用者先看到「舊→新」跟斷連提醒再決定。
  async function renameDir(f: WorkFolder) {
    if (!(await guardEdit()) || busy) return;
    const slug = slugify(slugDrafts[f.dir] ?? segOf(f.dir));
    if (!slug) { setNotice("路徑名稱不能空白。"); return; }
    const to = `${parentOf(f.dir)}/${slug}`;
    if (to === f.dir) { setNotice("名稱沒有變。"); return; }
    setConfirmRename({ f, to });
  }

  async function confirmRenamePath() {
    if (!confirmRename) return;
    const { f, to } = confirmRename;
    setConfirmRename(null);
    if (!(await guardEdit()) || busy) return;
    setBusy(true); setError(null);
    try {
      const r = await api.renamePath(f.dir, to);
      setNotice(`已改路徑並搬移 ${r.count} 個檔案。記得更新指向舊網址的連結。`);
      props.onReload();
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  async function doDelete(f: WorkFolder) {
    setConfirmDel(null);
    if (!(await guardEdit()) || busy) return;
    setBusy(true); setError(null);
    try {
      const r = await api.deletePath(f.dir);
      setNotice(`已刪除路徑（${r.count} 個檔案）。`);
      props.onReload();
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  // 建立這個路徑自己的「內文」——一支 index.md，Docusaurus 慣例會拿它當路徑的頁面。
  async function createIndexDoc(f: WorkFolder) {
    if (!(await guardEdit()) || busy) return;
    setBusy(true); setError(null);
    try {
      const path = `${f.dir}/index.md`;
      const content = stringify(
        { sidebar_label: f.label, sidebar_position: 1, draft: true },
        `# ${f.label}\n\n在這裡寫這個路徑的介紹…\n`
      );
      await api.save({ path, content, message: `content: create index for ${f.dir}` });
      setNotice(effMode(f) === "auto" ? "已建立內文（index.md）。" : "已建立內文（index.md）。把「頁面」改成「自動」它才會生效。");
      props.onReload();
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  // 刪除這個路徑的「內文」檔（慣例索引檔）；若 _category_.json 的 link 指向它，一併移除避免斷連。
  async function doDeleteDoc(f: WorkFolder) {
    setConfirmDelDoc(null);
    if (!(await guardEdit()) || busy || !f.indexDoc) return;
    const doc = f.docs.find((d) => d.path === f.indexDoc);
    if (!doc?.sha) { setError("找不到內文檔資訊，請先重新整理。"); return; }
    setBusy(true); setError(null);
    try {
      await api.remove({ path: f.indexDoc, sha: doc.sha, message: `content: delete index ${f.indexDoc}` });
      if (f.linkMode === "doc" && f.linkDocId === docIdOf(f.indexDoc) && f.sha) {
        const obj = { ...f.raw };
        delete obj.link;
        await api.save({ path: f.file, content: JSON.stringify(obj, null, 2) + "\n", sha: f.sha, message: `content: clear link for ${f.dir}` });
      }
      setNotice("已刪除內文。");
      props.onReload();
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  // ---- rows ----
  // 被拖的列保留在原位（dragsrc 半透明）——不能 filter 掉，見上面拖曳區的註解。
  const dragging = dragDir != null;
  const display = rootSeq;
  const rows: JSX.Element[] = [];
  let p = 0;
  const gap = () => {
    if (dragging) rows.push(<div key={"g" + p} className={`nf-gap ${hoverGap === p ? "active" : ""}`} data-pidx={p} />);
    p++;
  };
  for (let i = 0; i <= display.length; i++) {
    gap();
    if (i === display.length) break;
    const node = display[i];
    if (node.kind === "doc") {
      rows.push(
        <div className="nf-oitem" key={node.doc.path}>
          <span className="nf-otitle">{node.doc.title}</span>
        </div>
      );
      continue;
    }
    const f = node.f;
    const open = panelOpen.has(f.dir) && !dragging;
    const isRenaming = renaming?.dir === f.dir;
    const em = effMode(f);
    rows.push(
      <div className={`fm-folder ${dragDir === f.dir ? "dragsrc" : ""} ${open ? "open" : ""}`} key={"dir:" + f.dir}>
        {/* 一個控件、兩種操作：拖曳排序（移動/放開/取消都在 window 監聽），或聚焦後用上下方向鍵移動 */}
        <button
          type="button"
          className="nf-drag"
          onPointerDown={(e) => onHandleDown(e, f.dir)}
          onKeyDown={(e) => onHandleKeyDown(e, f.dir)}
          aria-label={`「${f.label}」的順序——拖曳，或聚焦後按上下方向鍵移動`}
          title="拖曳排序（Esc 取消），或按 ↑↓ 移動"
        >
          ⠿
        </button>
        <span className="fm-folder__ico" aria-hidden>▤</span>
        {isRenaming ? (
          <input
            className="fm-rename"
            value={renaming!.text}
            autoFocus
            onChange={(e) => setRenaming({ dir: f.dir, text: e.target.value })}
            onBlur={commitRename}
            onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenaming(null); }}
          />
        ) : (
          <button className="fm-label" type="button" onClick={() => setRenaming({ dir: f.dir, text: f.label })} aria-label={`改顯示名稱：${f.label}`}>
            <span className="fm-label__text">{f.label}</span>
            <span className="fm-label__pencil" aria-hidden>✎</span>
            {isDirty(f) && <span className="fm-dot" aria-label="已修改" />}
          </button>
        )}
        {em === "index" && <span className="fm-badge" title="自動生成索引頁">索引頁</span>}
        {em === "doc" && <span className="fm-badge" title="指定文章當這個路徑的頁面">內文</span>}
        {em === "auto" && f.indexDoc && (
          <span className="fm-badge" title={`慣例索引檔 ${f.indexDoc.split("/").pop()} 會當成路徑的頁面`}>內文</span>
        )}
        <span className="fm-count">{f.docs.length} 篇</span>
        <button
          type="button"
          className={`fm-gear ${open ? "on" : ""}`}
          onClick={() => togglePanel(f.dir)}
          aria-expanded={open}
          aria-label="路徑設定"
        >
          ▾
        </button>
      </div>
    );

    if (open) {
      const slug = slugDrafts[f.dir] ?? segOf(f.dir);
      const dangerIsOpen = dangerOpen.has(f.dir);
      const slugChanged = slugify(slug) !== segOf(f.dir) && !!slugify(slug);
      rows.push(
        <div className="fm-panel" key={"panel:" + f.dir}>
          {/* 這個路徑底下有哪些文章——最常被查看，放最上面 */}
          <div className="fm-set">
            <span className="fm-set__label">這個路徑的文章 <em>{f.docs.length} 篇</em></span>
            {f.docs.length ? (
              <ul className="fm-doclist">
                {f.docs.map((d) => (
                  <li key={d.path} className={`fm-doc ${d.status === "draft" ? "draft" : ""}`}>
                    <button className="fm-doc__title" onClick={() => props.onOpenDoc(d.path)}>
                      {d.title || d.path.split("/").pop()}
                    </button>
                    {d.path === f.indexDoc && <span className="fm-badge">內文</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="fm-set__hint">這個路徑還沒有文章。</p>
            )}
          </div>

          <div className="fm-set">
            <span className="fm-set__label">這個路徑的頁面</span>
            <div className="fm-seg" role="group">
              {([["auto", "自動"], ["none", "無"], ["index", "索引頁"], ["doc", "指定文章"]] as [LinkMode, string][]).map(([m, lbl]) => (
                <button
                  key={m}
                  type="button"
                  className={f.linkMode === m ? "on" : ""}
                  onClick={() => updateFolder(f.dir, { linkMode: m })}
                >
                  {lbl}
                </button>
              ))}
            </div>
            {f.linkMode === "index" && (
              <div className="fm-idx">
                <input
                  className="fm-slug__in"
                  value={f.linkTitle ?? ""}
                  placeholder="索引頁標題（留空＝用路徑名稱）"
                  onChange={(e) => updateFolder(f.dir, { linkTitle: e.target.value })}
                />
                <textarea
                  className="fm-idxdesc"
                  value={f.linkDesc ?? ""}
                  rows={2}
                  placeholder="索引頁說明——顯示在自動索引頁上方的內文"
                  onChange={(e) => updateFolder(f.dir, { linkDesc: e.target.value })}
                />
              </div>
            )}
            {f.linkMode === "doc" && (
              <select
                className="fm-docsel"
                value={f.linkDocId ?? ""}
                onChange={(e) => updateFolder(f.dir, { linkDocId: e.target.value || null })}
              >
                <option value="">選一篇當頁面…</option>
                {f.docs.map((d) => (
                  <option key={d.path} value={d.id}>{d.title}</option>
                ))}
              </select>
            )}
            <p className="fm-set__hint">
              {f.linkMode === "auto" &&
                (f.indexDoc
                  ? `依 Docusaurus 慣例：${f.indexDoc.split("/").pop()} 會自動當這個路徑的頁面（有內文）。`
                  : "沒有慣例索引檔（index／README／同名），點路徑只展開子項。放一支進來就會自動變成它的頁面。")}
              {f.linkMode === "none" && "明確關閉（link: null）——就算有慣例索引檔也不當頁面，點路徑只展開子項。"}
              {f.linkMode === "index" && "自動生成一頁列出底下文章；上面的標題／說明會顯示在那一頁。"}
              {f.linkMode === "doc" && (f.docs.length ? "用選定的文章當作這個路徑的頁面（有內文）。" : "這個路徑還沒有文章可指定。")}
            </p>
            {f.indexDoc && em !== "auto" && !(em === "doc" && f.linkDocId === docIdOf(f.indexDoc)) && (
              <p className="fm-warn">
                {em === "none" ? (
                  <>有慣例索引檔 <code>{f.indexDoc.split("/").pop()}</code>（含內文），但「無」會把它關掉。想用它當頁面，選「自動」。</>
                ) : (
                  <>有慣例索引檔 <code>{f.indexDoc.split("/").pop()}</code>（含內文），但「頁面」設成別的——這個明確設定會蓋過它。想用它，改選「自動」或「指定文章」直接選它。</>
                )}
              </p>
            )}
          </div>

          {/* 外觀：兩個開關合併一組，減少獨立決策點 */}
          <div className="fm-set">
            <span className="fm-set__label">外觀</span>
            <div className="fm-toggles">
              <button
                type="button"
                className={`fm-toggle ${f.collapsed ? "on" : ""}`}
                onClick={() => updateFolder(f.dir, { collapsed: !f.collapsed })}
              >
                網站上{f.collapsed ? "預設收合" : "預設展開"}
              </button>
              <button
                type="button"
                className={`fm-toggle ${f.collapsible === false ? "" : "on"}`}
                onClick={() => updateFolder(f.dir, { collapsible: f.collapsible === false ? true : false })}
              >
                {f.collapsible === false ? "不可收合" : "可收合"}
              </button>
            </div>
          </div>

          {/* 路徑的內文（＝路徑自己的頁面文章）：有 → 可編輯/刪除；沒有 → 可建立 */}
          <div className="fm-set">
            <span className="fm-set__label">路徑內文 <em>這個路徑自己的頁面文章</em></span>
            {f.indexDoc ? (
              <div className="fm-inline">
                <span className="fm-inline__name" title={f.indexDoc}>▤ {f.indexDoc.split("/").pop()}</span>
                <button className="btn small ghost" onClick={() => props.onOpenDoc(f.indexDoc!)}>編輯內文</button>
                <button className="fm-del sm" disabled={busy || dirty} onClick={() => setConfirmDelDoc(f)}>
                  刪除內文
                </button>
                <InstantBadge />
              </div>
            ) : (
              <div className="fm-inline">
                <span className="fm-set__hint">還沒有內文（會建立 <code>index.md</code>）。</span>
                <button className="btn small primary" disabled={busy || dirty} onClick={() => createIndexDoc(f)}>
                  建立內文
                </button>
                <InstantBadge />
              </div>
            )}
            {dirty && <p className="fm-set__hint fm-set__hint--warn">上面有未儲存的變更，先「儲存」或「捨棄」才能建立／刪除內文。</p>}
          </div>

          {/* 危險區：會立即 commit、且影響較大的操作，預設收起來避免跟上面的暫存編輯混在一起 */}
          <div className="fm-danger">
            <button type="button" className={`fm-danger__head ${dangerIsOpen ? "on" : ""}`} onClick={() => toggleDanger(f.dir)} aria-expanded={dangerIsOpen}>
              <span>危險區</span>
              <em>路徑名稱・刪除路徑</em>
              <span className={`fm-danger__chev ${dangerIsOpen ? "open" : ""}`} aria-hidden>▾</span>
            </button>
            {dangerIsOpen && (
              <div className="fm-danger__body">
                <div className="fm-set">
                  <span className="fm-set__label">路徑名稱 <em>網址的一段——改了網址會變</em></span>
                  <div className="fm-slug">
                    <span className="fm-slug__base">{parentOf(f.dir)}/</span>
                    <input
                      className="fm-slug__in"
                      value={slug}
                      spellCheck={false}
                      autoCapitalize="off"
                      autoCorrect="off"
                      onChange={(e) => setSlugDrafts((m) => ({ ...m, [f.dir]: e.target.value }))}
                      placeholder={segOf(f.dir)}
                    />
                    <button
                      className="btn small ghost"
                      disabled={busy || dirty || !slugChanged}
                      onClick={() => renameDir(f)}
                    >
                      改路徑
                    </button>
                    <InstantBadge />
                  </div>
                  {dirty && <p className="fm-set__hint fm-set__hint--warn">先儲存或捨棄上面的變更，才能改路徑。</p>}
                </div>
                <div className="fm-inline">
                  <button className="fm-del" disabled={busy || dirty} onClick={() => setConfirmDel(f)}>
                    刪除這個路徑…
                  </button>
                  <InstantBadge />
                </div>
                {dirty && <p className="fm-set__hint fm-set__hint--warn">先儲存或捨棄上面的變更，才能刪除路徑。</p>}
              </div>
            )}
          </div>
        </div>
      );
    }
  }

  return (
    <div className="editor">
      <header className="topbar">
        <button className="btn ghost" onClick={props.onClose}>‹ 返回</button>
        <div className="grow"><div className="path">路徑編輯 · Path</div></div>
        {dirty && <button className="btn ghost" onClick={discard} disabled={busy}>捨棄</button>}
        <button className="btn primary" onClick={save} disabled={busy || !dirty}>
          {busy ? "處理中…" : dirty ? `儲存（${changes.length}）` : "已是最新"}
        </button>
      </header>

      {error && <div className="banner error">{error}</div>}
      {notice && <div className="banner notice">{notice}</div>}

      <div className="newform">
        <div className="nf-field">
          <span className="nf-label">哪個分類？</span>
          <div className="nf-cats">
            {cats.map((c) => (
              <button key={c} type="button" className={`chip ${cat === c ? "on" : ""}`} onClick={() => setCat(c)}>
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="nf-field">
          <span className="nf-label">
            路徑 <em>拖 ⠿ 排序（或聚焦後按 ↑↓）、點名稱改名、▾ 展開更多設定；灰字是分類根目錄的文章</em>
          </span>
          {work.length === 0 ? (
            <p className="nf-note">這個分類還沒有子路徑。用下面的「新增路徑」建立一個。</p>
          ) : (
            <div ref={treeRef} className={`nf-tree ${dragging ? "dragging" : ""}`}>
              {rows}
            </div>
          )}
          {creating == null ? (
            <>
              <button className="fm-add" type="button" onClick={() => setCreating("")} disabled={busy || dirty}>
                ＋ 新增路徑
              </button>
              {dirty && <p className="fm-set__hint fm-set__hint--warn">先儲存或捨棄變更，才能新增路徑。</p>}
            </>
          ) : (
            <div className="fm-newpath">
              <input
                className="fm-slug__in"
                value={creating}
                autoFocus
                placeholder="路徑名稱，例如 資安筆記"
                onChange={(e) => setCreating(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") createPath(); if (e.key === "Escape") setCreating(null); }}
              />
              <span className="fm-slug__hint">→ {catInfo.base}/{slugify(creating) || "…"}</span>
              <button className="btn small primary" onClick={createPath} disabled={busy || !slugify(creating)}>建立</button>
              <button className="btn small ghost" onClick={() => setCreating(null)} disabled={busy}>取消</button>
              <InstantBadge />
            </div>
          )}
        </div>

        <p className="nf-note">
          <b>名稱・排序・收合・頁面</b>只改各路徑的 <code>_category_.json</code>，按「儲存」才生效，隨時可「捨棄」收回。
          標示 <b>⚡ 立即</b> 的按鈕（新增路徑、路徑內文、危險區）不受上面「儲存／捨棄」影響，點下去就馬上 commit，各自有需要時的確認框。
          記得回首頁按 <b>Deploy</b> 才會上線。
        </p>
      </div>

      {confirmRename && (
        <div className="sheet-backdrop center" onClick={() => setConfirmRename(null)}>
          <div className="confirm" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
            <p className="confirm-title">改路徑名稱？</p>
            <p className="confirm-body">
              <span className="fm-rename-diff">
                <code>{confirmRename.f.dir}</code>
                <span className="fm-rename-diff__arrow" aria-hidden>→</span>
                <code>{confirmRename.to}</code>
              </span>
              會搬移底下 <b>{confirmRename.f.docs.length} 個檔案</b>，<b>網址會變</b>；
              指向舊網址（含網站上任何引用這個路徑的連結）的連結會<b>失效</b>，需要自己更新。
            </p>
            <div className="confirm-actions">
              <button className="btn small ghost" onClick={() => setConfirmRename(null)}>取消</button>
              <button className="btn small danger" onClick={confirmRenamePath}>確認改路徑</button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="sheet-backdrop center" onClick={() => setConfirmDel(null)}>
          <div className="confirm" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
            <p className="confirm-title">刪除路徑「{confirmDel.label}」？</p>
            <p className="confirm-body">
              會刪掉這個路徑與底下的 <b>{confirmDel.docs.length} 篇文章</b>，<b>無法復原</b>。
            </p>
            <div className="confirm-actions">
              <button className="btn small ghost" onClick={() => setConfirmDel(null)}>取消</button>
              <button className="btn small danger" onClick={() => doDelete(confirmDel)}>刪除</button>
            </div>
          </div>
        </div>
      )}

      {confirmDelDoc && (
        <div className="sheet-backdrop center" onClick={() => setConfirmDelDoc(null)}>
          <div className="confirm" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
            <p className="confirm-title">刪除「{confirmDelDoc.label}」的內文？</p>
            <p className="confirm-body">
              會移除 <b>{confirmDelDoc.indexDoc?.split("/").pop()}</b>（這個路徑的頁面文章），<b>無法復原</b>。其他文章不受影響。
            </p>
            <div className="confirm-actions">
              <button className="btn small ghost" onClick={() => setConfirmDelDoc(null)}>取消</button>
              <button className="btn small danger" onClick={() => doDeleteDoc(confirmDelDoc)}>刪除內文</button>
            </div>
          </div>
        </div>
      )}

      {ghost && (
        <div className="nf-ghost" style={{ left: ghost.x, top: ghost.y }}>
          {work.find((f) => f.dir === dragDir)?.label ?? "路徑"}
          <span className="nf-ghost__hint">放開＝放置 · Esc＝取消</span>
        </div>
      )}
    </div>
  );
}

function keyOf(n: Node): string {
  return n.kind === "doc" ? n.doc.path : n.f.dir;
}
